
import React from 'react';
import { format } from 'date-fns';
import { Calendar } from 'lucide-react';
import { isValidDateString } from '@/utils/dateValidation';

export interface RecentActivityTimelineProps {
  dates: string[];
}

const RecentActivityTimeline: React.FC<RecentActivityTimelineProps> = ({ dates }) => {
  if (!dates || dates.length === 0) {
    return (
      <div className="flex items-center justify-center p-6 text-muted-foreground">
        No recent activity data available
      </div>
    );
  }

  // Parse dates and handle timezone offsets
  const parseDateStr = (dateStr: string): Date | null => {
    try {
      // Handle dates with timezone offsets like "2025-02-11+01:00"
      if (dateStr.includes('+') && !dateStr.includes('T')) {
        // Convert to ISO format by adding time
        const parts = dateStr.split('+');
        dateStr = `${parts[0]}T00:00:00+${parts[1]}`;
      }
      
      const date = new Date(dateStr);
      return isNaN(date.getTime()) ? null : date;
    } catch (e) {
      console.error("Error parsing date:", dateStr, e);
      return null;
    }
  };

  // Sort dates in descending order (most recent first) and filter invalid dates
  const sortedDates = [...dates]
    .filter(dateStr => {
      if (!dateStr) return false;
      const date = parseDateStr(dateStr);
      return date !== null;
    })
    .sort((a, b) => {
      const dateA = parseDateStr(a);
      const dateB = parseDateStr(b);
      
      if (!dateA || !dateB) return 0;
      return dateB.getTime() - dateA.getTime();
    });

  if (sortedDates.length === 0) {
    return (
      <div className="flex items-center justify-center p-6 text-muted-foreground">
        No valid activity dates available
      </div>
    );
  }
  
  return (
    <div className="space-y-4">
      {sortedDates.slice(0, 10).map((dateStr, index) => {
        // Parse and validate the date
        let formattedDate = "Unknown date";
        try {
          const date = parseDateStr(dateStr);
          if (date) {
            formattedDate = format(date, 'PPP');
          }
        } catch (error) {
          console.error(`Error formatting date: ${dateStr}`, error);
        }

        return (
          <div key={index} className="flex items-start gap-4">
            <div className="mt-0.5 flex h-8 w-8 items-center justify-center rounded-full bg-primary/10">
              <Calendar className="h-4 w-4 text-primary" />
            </div>
            <div className="space-y-1">
              <p className="text-sm font-medium">Activity recorded</p>
              <p className="text-sm text-muted-foreground">{formattedDate}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default RecentActivityTimeline;
